import type { ReactNode } from "react";
import { Input } from "antd";
import { useI18n } from "../i18n";
import { FormModal } from "./FormModal";

/**
 * Confirmation modal for irreversible actions.
 *
 * When `expectedText` is supplied the primary action stays disabled until the
 * user types it exactly — used for revoking credentials and similar
 * destructive operations where an accidental click is costly.
 */
export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  expectedText,
  confirmationText,
  onConfirmationText,
  onConfirm,
  onCancel,
  busy = false,
  danger = true,
  error,
}: {
  open: boolean;
  title: ReactNode;
  description: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /** When set, the user must type this value before confirming. */
  expectedText?: string;
  confirmationText?: string;
  onConfirmationText?: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  danger?: boolean;
  error?: Error | null;
}) {
  const { locale } = useI18n();
  const isChinese = locale === "zh-CN";
  const needsTyping = expectedText !== undefined;
  const satisfied = !needsTyping || confirmationText === expectedText;

  return (
    <FormModal
      open={open}
      title={title}
      description={description}
      onCancel={onCancel}
      onSubmit={onConfirm}
      submitLabel={confirmLabel ?? (isChinese ? "确认" : "Confirm")}
      cancelLabel={cancelLabel}
      busy={busy}
      disabled={!satisfied}
      danger={danger}
      error={error}
    >
      {needsTyping ? (
        <label className="portal-confirm-modal__field">
          <span>
            {isChinese ? "请输入 " : "Type "}
            <strong>{expectedText}</strong>
            {isChinese ? " 以确认" : " to confirm"}
          </span>
          <Input
            autoFocus
            value={confirmationText}
            onChange={(event) => onConfirmationText?.(event.target.value)}
            placeholder={expectedText}
          />
        </label>
      ) : null}
    </FormModal>
  );
}
